// src/pages/UpdateProfile.jsx
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { updateUser, checkUsernameAvailability } from '../api/userApi';
import { useNavigate } from 'react-router-dom';
import { Bounce, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  CountrySelector,
  MobileCodeSelector,
} from "../components/Selectors/Country";
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import config from '../config';

const DEFAULT_MOBILE_CODE = '961';
const DEFAULT_COUNTRY = 'LB';

const normalizeMobileCode = (value) => {
  if (value == null) return '';
  const digits = String(value).replace(/\D/g, '');
  return digits;
};

const normalizeCountry = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return '';
};

export default function UpdateProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, setUserData } = useAuth();
  const [usernameCheck, setUsernameCheck] = React.useState({
    checking: false,
    message: '',
    suggestion: '',
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: '',
      displayName: '',
      username: '',
      mobileCode: DEFAULT_MOBILE_CODE,
      mobile: '',
      country: DEFAULT_COUNTRY,
      dob: '',
      gender: '',
      bio: '',
      profession: '',
      website: '',
    },
  });

  // On mount, populate form with userData
  useEffect(() => {
    if (!userData) return;

    const normalizedMobileCode = normalizeMobileCode(userData.country_code);
    const normalizedCountry = normalizeCountry(
      userData.country || userData.country_abbr || userData.countryCode
    );

    reset({
      email: userData.user_email || '',
      displayName: userData.full_name || '',
      username: userData.user_name || '',
      mobileCode: normalizedMobileCode || DEFAULT_MOBILE_CODE,
      mobile: userData.user_mobile || '',
      country: normalizedCountry || DEFAULT_COUNTRY,
      dob: userData.dob ? userData.dob.split('T')[0] : '',
      gender: userData.gender != null ? String(userData.gender) : '',
      bio: userData.bio || '',
      profession: userData.profession || '',
      website: userData.website || '',
    });
    setUsernameCheck({ checking: false, message: '', suggestion: '' });
  }, [userData, reset]);

  const usernameValue = watch('username');

  useEffect(() => {
    setUsernameCheck({ checking: false, message: '', suggestion: '' });
  }, [usernameValue]);

  const handleUsernameBlur = async () => {
    const username = (usernameValue || '').trim();
    const currentUsername = (userData?.user_name || '').trim();

    if (!username || username === currentUsername) {
      setUsernameCheck({ checking: false, message: '', suggestion: '' });
      return;
    }

    setUsernameCheck({ checking: true, message: '', suggestion: '' });

    try {
      const result = await checkUsernameAvailability(username);

      if (result?.valid) {
        setUsernameCheck({
          checking: false,
          message: t('usernameAvailable', { defaultValue: 'Username is available.' }),
          suggestion: '',
        });
      } else {
        setUsernameCheck({
          checking: false,
          message: t('usernameTaken', { defaultValue: 'Username is already taken.' }),
          suggestion: result?.suggestions || '',
        });
      }
    } catch (error) {
      setUsernameCheck({
        checking: false,
        message: error.message || t('usernameCheckError', { defaultValue: 'Could not check username.' }),
        suggestion: '',
      });
    }
  };

  const applyUsernameSuggestion = () => {
    if (!usernameCheck.suggestion) return;

    setValue('username', usernameCheck.suggestion, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setUsernameCheck({ checking: false, message: '', suggestion: '' });
  };

  const onProfileSubmit = async (data) => {
    try {
      const response = await fetch(`${config.apiUrl}/country`);
      const fetchountries = await response.json();
      const payload = {
        full_name: data.displayName,
        user_name: data.username,
        country_code: data.mobileCode,
        user_mobile: data.mobile,
        countryId: 
        fetchountries.filter((country) => country.abbr === data.country)[0]
          ?.id || 1,
        dob: data.dob || null,
        gender: parseInt(data.gender, 10) || null,
        bio: data.bio || null,
        profession: data.profession || null,
        website: data.website || null,
      };
      const updated = await updateUser(userData.id, payload);
      setUserData(updated);
      toast.success(t('profileUpdateSuccess'), { transition: Bounce });
      navigate(`/`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('updateError'), { transition: Bounce });
    }
  };

  if (!userData) {
    return <p className="p-4">{t('noUser')}</p>;
  }

  return (
    <div className="max-w-xl mx-auto p-4 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-4">{t('Update Profile')}</h2>
      <form onSubmit={handleSubmit(onProfileSubmit)} className="space-y-4">
        {/* Email (read-only) */}
        <input
          type="email"
          placeholder={t('email')}
          {...register('email')}
          className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff] bg-gray-100"
          readOnly
        />

        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('Full Name')}</label>
          <input
            type="text"
            {...register('displayName', { required: t('usernamereq') })}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          />
          {errors.displayName && <p className="text-xs text-red-500">{errors.displayName.message}</p>}
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('username')}</label>
          <input
            type="text"
            {...register('username', { required: t('usernamereq') })}
            onBlur={(e) => {
              register('username', { required: t('usernamereq') }).onBlur(e);
              handleUsernameBlur();
            }}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          />
          {errors.username && <p className="text-xs text-red-500">{errors.username.message}</p>}
          {!errors.username && usernameCheck.checking && (
            <p className="text-xs text-gray-500">
              {t('checkingUsername', { defaultValue: 'Checking username...' })}
            </p>
          )}
          {!errors.username && !usernameCheck.checking && usernameCheck.message && (
            <p className={`text-xs ${usernameCheck.suggestion ? 'text-red-500' : 'text-green-600'}`}>
              {usernameCheck.message}
            </p>
          )}
          {!errors.username && usernameCheck.suggestion && (
            <button
              type="button"
              onClick={applyUsernameSuggestion}
              className="text-xs font-medium text-[#47b5ff] underline"
            >
              {t('useSuggestion', {
                defaultValue: `Use suggestion: ${usernameCheck.suggestion}`,
              })}
            </button>
          )}
        </div>

        {/* Mobile Code + Mobile */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('mobile')}</label>
          <div className="flex space-x-2">
            <Controller
              name="mobileCode"
              control={control}
              rules={{ required: t('codeRequired') }}
              render={({ field, fieldState: { error } }) => (
                <MobileCodeSelector {...field} error={error?.message} />
              )}
            />
            <input
              type="tel"
              inputMode="numeric"
              {...register('mobile', {
                required: t('mobilereg'),
                pattern: { value: /^[0-9]+$/, message: t('mobileval') },
                minLength: { value: 6, message: t('mobileminlength') },
              })}
              className="flex-1 p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
            />
          </div>
          {(errors.mobileCode || errors.mobile) && (
            <p className="text-xs text-red-500">
              {errors.mobileCode?.message || errors.mobile?.message}
            </p>
          )}
        </div>

        {/* Country */}
        <div>
          <Controller
            name="country"
            control={control}
            rules={{ required: t('countryReq') }}
            render={({ field, fieldState: { error } }) => (
              <CountrySelector {...field} error={error?.message} />
            )}
          />
        </div>

        {/* DOB */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('Date of Birth')}</label>
          <input
            type="date"
            {...register('dob')}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('gender')}</label>
          <select
            {...register('gender', { required: t('genderReq') })}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          >
            <option value="">{t('selectGender')}</option>
            <option value="1">{t('male')}</option>
            <option value="2">{t('female')}</option>
            <option value="3">{t('other')}</option>
          </select>
          {errors.gender && <p className="text-xs text-red-500">{errors.gender.message}</p>}
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('bio')}</label>
          <textarea
            {...register('bio')}
            rows={3}
            className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#47b5ff]"
            placeholder={t('enterBio')}
          />
        </div>

        {/* Profession */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('profession')}</label>
          <select
            {...register('profession')}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          >
            <option value="">{t('selectProfession')}</option>
            <option value="Athlete">{t('Athlete')}</option>
            <option value="Coach">{t('Coach')}</option>
            <option value="Gamer">{t('Gamer')}</option>
            <option value="Teacher">{t('Teacher')}</option>
            <option value="Boxer">{t('Boxer')}</option>
            {/* add more */}
          </select>
        </div>

        {/* Website */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('website')}</label>
          <input
            type="url"
            {...register('website', {
              pattern: {
                value: /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}/i,
                message: t('websiteVal'),
              },
            })}
            className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
          />
          {errors.website && <p className="text-xs text-red-500">{errors.website.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#ff8000] text-white py-3 rounded-full disabled:opacity-50"
        >
          {isSubmitting ? <Loader2 className="animate-spin w-5 h-5 mx-auto" /> : t('updateProfile')}
        </button>
      </form>
    </div>
  );
}
